import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CalendarClock, Download, Eye, Plus, Trash2, Users } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { AttachmentChips, AttachmentUpload } from '../components/gsoc/Attachments';

const selectCls = 'h-10 w-full px-3 text-sm rounded-md border border-line bg-surface text-fg';
const err = (e, f) => toast.error(e?.response?.data?.detail || f);
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'short' }) : '—');

const blank = { title: '', body: '', client_id: '', site_id: '', expires_at: '', work_order_number: '', account_number: '', is_active: true, is_urgent: false };

export default function GSOCPassOn() {
  const perms = useEffectivePermissions();
  const isAdmin = perms.role === 'admin';
  const canAdd = isAdmin || perms.can('gsoc_passon', 'add');
  const canEdit = isAdmin || perms.can('gsoc_passon', 'edit');
  const canDelete = isAdmin || perms.can('gsoc_passon', 'delete');
  const canSeeAcks = isAdmin || perms.can('gsoc_passon', 'view');
  const canExport = isAdmin || perms.can('gsoc', 'export');
  const [exporting, setExporting] = useState(false);

  const [notes, setNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [draft, setDraft] = useState(blank);
  const [acks, setAcks] = useState({ noteId: null, rows: [] });

  const load = () => api.get('/gsoc/pass-on', { params: { include_inactive: true } })
    .then(({ data }) => setNotes(data)).catch(() => {});

  useEffect(() => {
    api.get('/gsoc/client-settings').then(({ data }) => setClients(data.filter((c) => c.activity_log_enabled))).catch(() => {});
    load();
  }, []);

  useEffect(() => {
    if (!draft.client_id) { setSites([]); return; }
    api.get('/gsoc/sites', { params: { client_id: draft.client_id } })
      .then(({ data }) => setSites(data)).catch(() => setSites([]));
  }, [draft.client_id]);

  const payload = (n) => ({
    title: n.title,
    body: n.body,
    client_id: n.client_id || null,
    site_id: n.site_id || null,
    expires_at: n.expires_at || null,
    work_order_number: n.work_order_number?.trim() || null,
    account_number: n.account_number?.trim() || null,
    is_active: n.is_active,
    is_urgent: !!n.is_urgent,
  });

  const create = async () => {
    if (!draft.title.trim() || !draft.body.trim()) return toast.error('Give the note a title and a body');
    try {
      await api.post('/gsoc/pass-on', payload(draft));
      setDraft(blank); load(); toast.success('Pass-on note posted');
    } catch (e) { err(e, 'Could not post the note'); }
  };

  const save = async (n) => {
    try { await api.put(`/gsoc/pass-on/${n.id}`, payload(n)); toast.success('Note saved'); load(); }
    catch (e) { err(e, 'Could not save the note'); }
  };

  const remove = async (n) => {
    try {
      await api.delete(`/gsoc/pass-on/${n.id}`);
      toast.success('Note archived — its acknowledgement trail is kept');
      load();
    } catch (e) { err(e, 'Could not archive the note'); }
  };

  const removeAttachment = async (a) => {
    try {
      await api.delete(`/gsoc/attachments/${a.id}`);
      toast.success(`${a.filename} removed from the note — still on file for anyone who acknowledged it`);
      load();
    } catch (e) { err(e, 'Could not remove the attachment'); }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get('/gsoc/pass-on-export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Pass_On_Log_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  const viewAcks = async (n) => {
    try {
      const { data } = await api.get(`/gsoc/pass-on/${n.id}/acknowledgements`);
      setAcks({ noteId: n.id, rows: data });
    } catch (e) { err(e, 'Could not load the acknowledgements'); }
  };

  return (
    <div className="space-y-5" data-testid="gsoc-passon-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">Pass-On Log</h1>
          <p className="text-sm text-fg-muted mt-1 max-w-2xl">
            Standing information every operator must read and acknowledge once per shift before they can log activity.
            Set an expiration date and the note drops out of the briefing on its own — the note and everyone who
            acknowledged it are kept permanently.
          </p>
        </div>
        {canExport && (
          <Button variant="outline" onClick={exportXlsx} disabled={exporting} data-testid="gsoc-passon-export-btn">
            <Download className="w-4 h-4 mr-2" aria-hidden="true" />{exporting ? 'Building...' : 'Export to Excel'}
          </Button>
        )}
      </div>

      {canAdd && (
        <div className="rounded-lg border border-line bg-surface p-4 space-y-3" data-testid="gsoc-passon-new">
          <p className="font-medium text-fg">Post a pass-on note</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="xl:col-span-2">
              <Label className="text-sm">Title</Label>
              <Input value={draft.title} className="mt-1" placeholder="Enter title here"
                     data-testid="gsoc-passon-title" onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
            </div>
            <div>
              <Label className="text-sm">Client (optional)</Label>
              <select className={`${selectCls} mt-1`} value={draft.client_id} data-testid="gsoc-passon-client"
                      onChange={(e) => setDraft((d) => ({ ...d, client_id: e.target.value, site_id: '' }))}>
                <option value="">All clients</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.client_name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm">Site (optional)</Label>
              <select className={`${selectCls} mt-1`} value={draft.site_id} disabled={!draft.client_id} data-testid="gsoc-passon-site"
                      onChange={(e) => setDraft((d) => ({ ...d, site_id: e.target.value }))}>
                <option value="">All sites</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <div>
              <Label className="text-sm">Work Order # (optional)</Label>
              <Input value={draft.work_order_number} className="mt-1" placeholder="WO-10482"
                     data-testid="gsoc-passon-work-order"
                     onChange={(e) => setDraft((d) => ({ ...d, work_order_number: e.target.value }))} />
            </div>
            <div>
              <Label className="text-sm">Account # (optional)</Label>
              <Input value={draft.account_number} className="mt-1" placeholder="ACCT-5567"
                     data-testid="gsoc-passon-account"
                     onChange={(e) => setDraft((d) => ({ ...d, account_number: e.target.value }))} />
            </div>
          </div>
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1">
              <Label className="text-sm">Message</Label>
              <Textarea rows={3} value={draft.body} className="mt-1" data-testid="gsoc-passon-body"
                        onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} />
            </div>
            <div className="lg:w-56 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={draft.is_urgent} data-testid="gsoc-passon-urgent"
                        onCheckedChange={(v) => setDraft((d) => ({ ...d, is_urgent: v }))} />
                <span className={draft.is_urgent ? 'text-danger font-semibold' : 'text-fg-muted'}>Mark urgent</span>
              </label>
              <div>
                <Label className="text-sm">Expires (optional)</Label>
                <Input type="date" value={draft.expires_at} className="mt-1" data-testid="gsoc-passon-expires"
                       onChange={(e) => setDraft((d) => ({ ...d, expires_at: e.target.value }))} />
              </div>
              <Button onClick={create} data-testid="gsoc-passon-create-btn"><Plus className="w-4 h-4 mr-1" />Post Note</Button>
              <p className="text-xs text-fg-muted">Post the note first, then attach files to it below.</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="gsoc-passon-list">
        {notes.length === 0 && <p className="text-sm text-fg-muted" data-testid="gsoc-passon-empty">No pass-on notes yet.</p>}
        {notes.map((n, i) => (
          <div key={n.id}
               className={`rounded-lg border bg-surface p-4 space-y-3 ${n.is_urgent && n.is_active && !n.is_expired
                 ? 'border-[hsl(var(--danger))] border-l-4' : 'border-line'}`}
               data-testid={`gsoc-passon-row-${i}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {n.is_urgent && (
                    <Badge className="bg-[hsl(var(--danger))] text-white border-0" data-testid={`gsoc-passon-urgent-badge-${i}`}>
                      <AlertTriangle className="w-3 h-3 mr-1" aria-hidden="true" />URGENT
                    </Badge>
                  )}
                  <p className={`font-semibold ${n.is_urgent ? 'text-danger' : 'text-fg'}`}>{n.title}</p>
                  {n.is_expired && <Badge className="bg-surface-sunken text-fg-muted border border-line" data-testid={`gsoc-passon-expired-${i}`}>expired</Badge>}
                  {n.is_archived && <Badge className="bg-surface-sunken text-fg-muted border border-line" data-testid={`gsoc-passon-archived-${i}`}>archived</Badge>}
                  {!n.is_active && !n.is_archived && <Badge className="bg-surface-sunken text-fg-muted border border-line">off</Badge>}
                </div>
                <p className="text-xs text-fg-muted mt-0.5">
                  {n.client_name} · {n.site_name} · posted by {n.created_by_name} {fmtStamp(n.created_at)}
                  {n.expires_at && <> · <CalendarClock className="w-3 h-3 inline" aria-hidden="true" /> expires {fmtDate(n.expires_at)}</>}
                  {n.work_order_number && <> · WO # <span className="text-fg font-medium">{n.work_order_number}</span></>}
                  {n.account_number && <> · Acct # <span className="text-fg font-medium">{n.account_number}</span></>}
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-fg-muted">
                <Users className="w-4 h-4" aria-hidden="true" />
                <span data-testid={`gsoc-passon-ackcount-${i}`}>{n.acknowledgement_count} acknowledgements</span>
              </div>
            </div>

            <Textarea rows={2} value={n.body} disabled={!canEdit}
                      onChange={(e) => setNotes((l) => l.map((x) => x.id === n.id ? { ...x, body: e.target.value } : x))} />

            {canEdit && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-fg-muted">Work Order #</Label>
                  <Input value={n.work_order_number || ''} className="h-9 mt-1" data-testid={`gsoc-passon-work-order-${i}`}
                         onChange={(e) => setNotes((l) => l.map((x) => x.id === n.id ? { ...x, work_order_number: e.target.value } : x))} />
                </div>
                <div>
                  <Label className="text-xs text-fg-muted">Account #</Label>
                  <Input value={n.account_number || ''} className="h-9 mt-1" data-testid={`gsoc-passon-account-${i}`}
                         onChange={(e) => setNotes((l) => l.map((x) => x.id === n.id ? { ...x, account_number: e.target.value } : x))} />
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <AttachmentChips items={n.attachments} testIdPrefix={`gsoc-passon-att-${i}`}
                               onRemove={canDelete ? (a) => removeAttachment(a) : undefined} />
              {canEdit && <AttachmentUpload noteId={n.id} onUploaded={load} testId={`gsoc-passon-attach-${i}`} />}
            </div>

            <div className="flex flex-wrap gap-3 items-center">
              {canEdit && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-fg-muted">Expires</Label>
                  <Input type="date" value={n.expires_at || ''} className="h-9 w-40" data-testid={`gsoc-passon-expires-${i}`}
                         onChange={(e) => setNotes((l) => l.map((x) => x.id === n.id ? { ...x, expires_at: e.target.value } : x))} />
                </div>
              )}              <div className="flex items-center gap-2">
                <Switch checked={!!n.is_urgent} disabled={!canEdit} data-testid={`gsoc-passon-urgent-${i}`}
                        onCheckedChange={(v) => setNotes((l) => l.map((x) => x.id === n.id ? { ...x, is_urgent: v } : x))} />
                <span className={`text-sm ${n.is_urgent ? 'text-danger font-semibold' : 'text-fg-muted'}`}>Urgent</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={n.is_active} disabled={!canEdit} data-testid={`gsoc-passon-active-${i}`}
                        onCheckedChange={(v) => setNotes((l) => l.map((x) => x.id === n.id ? { ...x, is_active: v } : x))} />
                <span className="text-sm text-fg-muted">In the briefing</span>
              </div>
              {canEdit && <Button variant="outline" size="sm" onClick={() => save(n)} data-testid={`gsoc-passon-save-${i}`}>Save</Button>}
              {canSeeAcks && (
                <Button variant="outline" size="sm" onClick={() => viewAcks(n)} data-testid={`gsoc-passon-acks-${i}`}>
                  <Eye className="w-4 h-4 mr-1" />Who read it
                </Button>
              )}
              {canDelete && !n.is_archived && (
                <Button variant="ghost" size="sm" onClick={() => remove(n)} data-testid={`gsoc-passon-delete-${i}`}>
                  <Trash2 className="w-4 h-4 mr-1" />Archive
                </Button>
              )}
            </div>

            {acks.noteId === n.id && (
              <div className="rounded-md border border-line bg-surface-sunken p-3" data-testid={`gsoc-passon-acklist-${i}`}>
                {acks.rows.length === 0 ? (
                  <p className="text-sm text-fg-muted">Nobody has acknowledged this yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="text-left py-1 font-medium text-fg-muted">Operator</th>
                        <th className="text-left py-1 font-medium text-fg-muted">Shift</th>
                        <th className="text-left py-1 font-medium text-fg-muted">Acknowledged</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acks.rows.map((a) => (
                        <tr key={a.id}>
                          <td className="py-1">{a.operator_name}</td>
                          <td className="py-1">{a.shift_label} · {fmtDate(a.shift_date)}</td>
                          <td className="py-1 text-fg-muted">{fmtStamp(a.acknowledged_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
